import { useRenderedBootHumanWait } from '#~/routes/boot-overlay/boot-budget-view.js';
import { Anchor } from '@studio/design-system/button';
import { Loader } from '@studio/design-system/loader';
import { useLatestFn } from '@studio/react-utils/hooks';
import { OperatingSystem, useOperatingSystem } from '@studio/react-utils/user-agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { isNotEmpty } from '@tryiris-ai/js-types';
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../icon.jsx';
import * as Wizard from '#~/agent/components/wizard/index.js';
import { useManifest } from '#~/agent/hooks.js';
import { Lang } from '#~/agent/index.js';
import { config } from '#~/config/config.js';
import { Paths } from '#~/routes/index.js';
import {
  useAnalytics,
  createDownloadLifecycleTracker,
  resolveDownloadOrigin,
  EVENT_CATEGORIES,
  EVENT_NAMES,
  INSTALLER_FAILURE_REASONS,
  UNKNOWN_DOWNLOAD_TARGET
} from '#~/analytics/index.js';

/** `<Select>` component. */
export const Select = (): ReactElement => {
  const [selection, setSelection] = useState<T_Maybe<OperatingSystem>>(useOperatingSystem());
  const navigate = useNavigate();
  const manifest = useManifest();

  const { track } = useAnalytics();
  const downloadTracker = createDownloadLifecycleTracker(track);

  // registration_completed has a single browser writer at setCurrentUser
  // (SPEC T1 §3.3). Keep AGENT_SETUP_STARTED here.
  useEffect(() => {
    track(EVENT_NAMES.AGENT_SETUP_STARTED, EVENT_CATEGORIES.ONBOARDING);
  }, [track]);

  // ENG-4310: this is where a user who cannot install is actually stopped. When
  // the selected OS has no artifacts the picker renders a disabled "Unavailable"
  // button and the user goes no further — no download route is ever reached — so
  // instrumenting only the download page would leave the whole population
  // silent. Reached without any hand-crafted link: this vendor offers Linux in
  // the picker and the manifest ships no Linux artifacts, and
  // `useOperatingSystem()` also lands a Linux visitor here on first render.
  const emitUnavailable = useLatestFn((os: OperatingSystem) => {
    // Same presence predicate as the download page's emit: '' is treated as
    // absent because the canonical receipt requires a meaningfully-present
    // environment and vendor, and a row missing one is rejected outright.
    if (!isNotEmpty(config.env) || !isNotEmpty(config.vendor)) {
      return;
    }

    downloadTracker.trackInstallerFailed({
      installer_failure_reason: INSTALLER_FAILURE_REASONS.ARTIFACT_UNAVAILABLE,
      ...resolveDownloadOrigin(),
      operating_system: os,
      // No architecture has been chosen yet — the dead end is the whole OS.
      architecture: UNKNOWN_DOWNLOAD_TARGET,
      environment: config.env,
      vendor: config.vendor
    });
  });

  // Keyed on `selection` (and the one-shot manifest) rather than running on every
  // render: a re-render must not add a second row, but genuinely switching to
  // another unsupported OS is a second dead end and does count again.
  useEffect(() => {
    if (!manifest || !selection) {
      return;
    }

    if (Object.keys(manifest[selection]).length === 0) {
      emitUnavailable(selection);
    }
  }, [manifest, selection]);

  // Manifest retrieval is machine work; only the loaded picker awaits a person.
  useRenderedBootHumanWait(Boolean(manifest));

  if (!manifest) {
    return <Loader />;
  }

  return (
    <Wizard.Root className='[@media(min-height:720px)]:gap-28' currentStep={2} totalSteps={4}>
      <Icon />

      <Wizard.Verbiage
        message={
          <Wizard.Verbiage.Message>
            Select your machine&rsquo;s specs.{' '}
            {/* Unsure which chip your computer has?{' '}
            <a href='https://tryiris.ai/help'>Click here</a> to learn how to determine which option
        is right for you. */}
          </Wizard.Verbiage.Message>
        }
        title='Select your Operating System'
      />

      <Wizard.ButtonBox className='flex w-[250px] flex-col gap-12 [&_button]:w-full [@media(min-height:720px)]:pt-20'>
        {((): ReactNode => {
          if (!selection) {
            return [OperatingSystem.Windows, OperatingSystem.macOS, OperatingSystem.Linux].map(
              (µ) => (
                <Wizard.Button
                  key={µ}
                  onClick={(): void => {
                    setSelection(µ);
                  }}
                >
                  {Lang.operatingSystem[µ]}
                </Wizard.Button>
              )
            );
          }

          const entries = Object.entries(manifest[selection]).map(([key, µ]) => (
            <Wizard.Button
              key={key}
              onClick={(): void => {
                // Track download event
                track(EVENT_NAMES.DOWNLOAD_APP, EVENT_CATEGORIES.ONBOARDING, {
                  operatingSystem: selection
                });
                navigate(Paths.agent.setup.download.resolve(selection, key));
              }}
            >
              {µ.label}
            </Wizard.Button>
          ));

          return entries.length ? (
            entries
          ) : (
            <Wizard.Button disabled variant='danger'>
              Unavailable
            </Wizard.Button>
          );
        })()}

        {!!selection && (
          <Anchor
            className='mt-auto text-primary [@media(min-height:720px)]:mt-24 [@media(min-height:800px)]:mt-64'
            onClick={(): void => {
              setSelection(undefined);
            }}
          >
            Looking for a different platform?
          </Anchor>
        )}
      </Wizard.ButtonBox>
    </Wizard.Root>
  );
};
